import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StorageKeyBuilder } from '../../../context/use-storage-key';
import { deriveDisplayState, isDone } from '../engine/derive-display-state';
import {
  nextPendingId,
  type StatusMap,
  useItemRegistry,
  useSettled,
} from '../engine/use-step-registry';
import type { ChecklistHandlers, ChecklistRootCtxValue } from '../types';

/**
 * Single-open expansion driven off status: opens the active item once settled, and on an open
 * item completing, advances to the next pending one. Manual toggles go through `setExpanded`.
 */
function useAutoExpansion(order: string[], statuses: StatusMap, settled: boolean) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const setExpanded = useCallback((id: string, expanded: boolean) => {
    setExpandedId((curr) => (expanded ? id : curr === id ? null : curr));
  }, []);

  const prevStatuses = useRef<StatusMap>(statuses);
  const didInit = useRef(false);
  useEffect(() => {
    const prev = prevStatuses.current;
    // Advance the snapshot only once settled, so a transient unsettled dip (e.g. a background
    // refetch flicking a status to `undefined` and back) can't replay as a false edge on re-settle.
    if (!settled) return;
    prevStatuses.current = statuses;

    if (!didInit.current) {
      didInit.current = true;
      setExpandedId(nextPendingId(order, statuses, 0));
      return;
    }

    // Advance when the open item itself just became done (completed or skipped); locked items can't
    // be open, so the open pending item is always the active one.
    setExpandedId((curr) => {
      if (curr && prev[curr] === 'pending' && isDone(statuses[curr])) {
        return nextPendingId(order, statuses, order.indexOf(curr) + 1);
      }
      return curr;
    });
  }, [settled, order, statuses]);

  return { expandedId, setExpanded };
}

/** Notifies the consumer of completion edges (after settle): per-item, all-complete boolean, finale. */
function useChecklistEvents(
  order: string[],
  statuses: StatusMap,
  settled: boolean,
  handlers: ChecklistHandlers,
  nodes: { current: Record<string, HTMLElement | null> },
) {
  // Ref so changing handler identity doesn't retrigger the effect.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const prevStatuses = useRef<StatusMap>(statuses);
  const didInit = useRef(false);
  // `null` until the first settle, so an incomplete initial load still reports its state once.
  const prevComplete = useRef<boolean | null>(null);

  useEffect(() => {
    const prev = prevStatuses.current;
    // Advance the snapshot only once settled, so a transient unsettled dip (e.g. a background
    // refetch flicking a status to `undefined` and back) can't replay as a false edge on re-settle.
    if (!settled) return;
    prevStatuses.current = statuses;

    const { onItemComplete, onCompletedChange, onAllComplete } = handlersRef.current;
    const isInitialSettle = !didInit.current;
    didInit.current = true;

    // Skip the initial settle so prior-complete items stay quiet. Genuine completion only — skipping
    // a step is not a per-item "completed" celebration.
    if (!isInitialSettle && onItemComplete) {
      for (const id of order) {
        if (prev[id] !== 'complete' && statuses[id] === 'complete') {
          onItemComplete({ id, element: nodes.current[id] ?? null });
        }
      }
    }

    // "All done" counts skipped steps as done, so skipping the last one still completes the list.
    const isComplete = order.length > 0 && order.every((id) => isDone(statuses[id]));
    if (prevComplete.current === null || isComplete !== prevComplete.current) {
      prevComplete.current = isComplete;
      onCompletedChange?.(isComplete);
      if (isComplete && !isInitialSettle) onAllComplete?.();
    }
    // `nodes` is a stable ref; listed to satisfy the linter, its `.current` is read lazily.
  }, [settled, order, statuses, nodes]);
}

export type ChecklistController = {
  ctx: ChecklistRootCtxValue;
  settled: boolean;
  /** Live completion counts for the progress header. */
  progress: { completed: number; total: number };
};

export function useChecklistController(
  handlers: ChecklistHandlers,
  storageKeyBuilder?: StorageKeyBuilder,
  gated = true,
): ChecklistController {
  const { order, statuses, syncItem, removeItem } = useItemRegistry();
  const settled = useSettled(order, statuses);
  const { expandedId, setExpanded } = useAutoExpansion(order, statuses, settled);

  // Item DOM nodes, in a ref so registering one never triggers a render. Read lazily when a
  // completion edge fires, to hand the consumer the node that just completed.
  const nodes = useRef<Record<string, HTMLElement | null>>({});
  const setNode = useCallback((id: string, element: HTMLElement | null) => {
    if (element) nodes.current[id] = element;
    else delete nodes.current[id];
  }, []);

  useChecklistEvents(order, statuses, settled, handlers, nodes);

  const displayStates = useMemo(
    () =>
      deriveDisplayState(
        order.map((id) => ({ id, status: statuses[id] })),
        gated,
      ),
    [order, statuses, gated],
  );

  const progress = useMemo(
    () => ({
      completed: order.reduce((n, id) => (isDone(statuses[id]) ? n + 1 : n), 0),
      total: order.length,
    }),
    [order, statuses],
  );

  const ctx = useMemo<ChecklistRootCtxValue>(
    () => ({
      syncItem,
      removeItem,
      setNode,
      hasItem: (id) => order.includes(id),
      expandedId,
      setExpanded,
      getDisplayState: (id) => displayStates.get(id),
      getStatus: (id) => statuses[id],
      storageKeyBuilder,
    }),
    [
      syncItem,
      removeItem,
      setNode,
      order,
      expandedId,
      setExpanded,
      displayStates,
      statuses,
      storageKeyBuilder,
    ],
  );

  return { ctx, settled, progress };
}
